// Screen: "Referrals"
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getReferralStats } from '../apiClient';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export default function ReferralPage() {
  const { token } = useAuth();
  const isMobile = useIsMobile();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    if (token) {
      getReferralStats(token).then(setStats);
    }
  }, [token]);

  const referralLink = stats?.referralCode
    ? `${window.location.origin}/signin?ref=${stats.referralCode}`
    : '';

  const copyCode = () => {
    navigator.clipboard.writeText(stats.referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareText = `Join me on Euchre Table! Use my referral code ${stats?.referralCode || ''} and we both get 50 Bucks. ${referralLink}`;

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '60px 32px',
      maxWidth: 600, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 24 }}>Invite Friends</h1>

      {!stats ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8b949e' }}>Loading...</div>
      ) : (
        <>
          {/* Referral Code Card */}
          <div style={{
            background: '#161b22', borderRadius: 12, padding: isMobile ? 20 : 28,
            border: '1px solid #30363d', marginBottom: 20, textAlign: 'center'
          }}>
            <p style={{ color: '#8b949e', fontSize: 13, marginBottom: 12 }}>Your Referral Code</p>
            <div style={{
              fontSize: isMobile ? 28 : 36, fontWeight: 700, letterSpacing: 4,
              color: '#fbbf24', marginBottom: 16, fontFamily: 'monospace'
            }}>
              {stats.referralCode}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={copyCode} style={{
                background: copied ? '#238636' : '#21262d', color: copied ? '#fff' : '#c9d1d9',
                border: '1px solid #30363d', borderRadius: 6, padding: '8px 16px',
                fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}>
                {copied ? 'Copied!' : 'Copy Code'}
              </button>
              <button onClick={copyLink} style={{
                background: copiedLink ? '#238636' : '#21262d', color: copiedLink ? '#fff' : '#c9d1d9',
                border: '1px solid #30363d', borderRadius: 6, padding: '8px 16px',
                fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}>
                {copiedLink ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

          {/* Share buttons */}
          <div style={{
            background: '#161b22', borderRadius: 12, padding: isMobile ? 16 : 20,
            border: '1px solid #30363d', marginBottom: 20
          }}>
            <p style={{ color: '#c9d1d9', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Share with friends</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a href={`sms:?body=${encodeURIComponent(shareText)}`} style={{
                flex: 1, minWidth: 100, textAlign: 'center', background: '#238636', color: '#fff',
                border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14,
                fontWeight: 600, textDecoration: 'none', cursor: 'pointer'
              }}>Text</a>
              <a href={`mailto:?subject=${encodeURIComponent('Join me on Euchre Table!')}&body=${encodeURIComponent(shareText)}`} style={{
                flex: 1, minWidth: 100, textAlign: 'center', background: '#58a6ff', color: '#fff',
                border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14,
                fontWeight: 600, textDecoration: 'none', cursor: 'pointer'
              }}>Email</a>
              <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" style={{
                flex: 1, minWidth: 100, textAlign: 'center', background: '#25d366', color: '#fff',
                border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14,
                fontWeight: 600, textDecoration: 'none', cursor: 'pointer'
              }}>WhatsApp</a>
            </div>
          </div>

          {/* How it works */}
          <div style={{
            background: '#161b22', borderRadius: 12, padding: isMobile ? 16 : 20,
            border: '1px solid #30363d', marginBottom: 20
          }}>
            <p style={{ color: '#c9d1d9', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>How it works</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ background: '#238636', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>1</span>
                <span style={{ color: '#8b949e', fontSize: 13 }}>Share your referral code or link with friends</span>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ background: '#238636', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>2</span>
                <span style={{ color: '#8b949e', fontSize: 13 }}>They sign up using your code — you both get 50 Bucks</span>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ background: '#238636', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>3</span>
                <span style={{ color: '#8b949e', fontSize: 13 }}>After they play 10 games, you both get a bonus 25 Bucks</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20
          }}>
            <div style={{
              background: '#161b22', borderRadius: 12, padding: isMobile ? 16 : 20,
              border: '1px solid #30363d', textAlign: 'center'
            }}>
              <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: '#58a6ff' }}>
                {stats.referralCount}
              </div>
              <div style={{ fontSize: 12, color: '#8b949e', marginTop: 4 }}>Friends Referred</div>
            </div>
            <div style={{
              background: '#161b22', borderRadius: 12, padding: isMobile ? 16 : 20,
              border: '1px solid #30363d', textAlign: 'center'
            }}>
              <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: '#fbbf24' }}>
                {stats.totalBucksEarned}
              </div>
              <div style={{ fontSize: 12, color: '#8b949e', marginTop: 4 }}>Bucks Earned</div>
            </div>
          </div>

          {/* Referred players list */}
          {stats.referredPlayers.length > 0 && (
            <div style={{
              background: '#161b22', borderRadius: 12, padding: isMobile ? 16 : 20,
              border: '1px solid #30363d'
            }}>
              <p style={{ color: '#c9d1d9', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                Referred Players
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {stats.referredPlayers.map(p => (
                  <div key={p.userId} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', background: '#0d1117', borderRadius: 6,
                    border: '1px solid #21262d'
                  }}>
                    <span style={{ fontSize: 14, color: '#c9d1d9' }}>{p.nickname || 'Player'}</span>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#8b949e' }}>{p.gamesPlayed} games</span>
                      {p.gamesPlayed >= 10 ? (
                        <span style={{ fontSize: 10, color: '#3fb950', fontWeight: 600 }}>Bonus earned!</span>
                      ) : (
                        <span style={{ fontSize: 10, color: '#8b949e' }}>{10 - p.gamesPlayed} games to bonus</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
